import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { logger } from '@shared/utils/functions.utils';
import * as qz from 'qz-tray';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class QzService {
  protected impresoras = new BehaviorSubject<string[]>([]);

  get impresoras$() {
    return this.impresoras.asObservable();
  }

  async init() {
    qz.security.setSignatureAlgorithm('SHA512');

    // 1. Configurar el certificado público
    qz.security.setCertificatePromise(function (resolve, reject) {
      fetch('certs/digital-certificate.pem', {
        cache: 'no-store',
        headers: { 'Content-Type': 'text/plain' },
      }).then(async function (data) {
        const text = await data.text();
        data.ok ? resolve(text) : reject(text);
      });
    });

    // 2. Configurar la firma digital (Llamada a tu API)
    qz.security.setSignaturePromise(function (toSign) {
      return function (resolve, reject) {
        fetch(`${environment.apiUrl}${environment.apiPreFix}/qz/sign?request=` + toSign, {
          cache: 'no-store',
          headers: { 'Content-Type': 'text/plain' },
        }).then(async function (data) {
          const text = await data.text();
          data.ok ? resolve(text) : reject(text);
        });
      };
    });

    // 3. Conectar al WebSocket de QZ Tray
    if (!qz.websocket.isActive()) {
      await qz.websocket
        .connect({
          host: 'localhost',
          port: {
            secure: [8181, 8282],
          },
          retries: 3,
          delay: 1000,
        })
        .then(async () => {
          logger('Conectado a QZ Tray');
          logger('Conectado, ahora pidiendo impresoras para disparar la firma...');
          await this.getPrinters(); // Esto obliga a QZ a pedir la firma
        })
        .catch((err) => {
          // QZ Tray no esta instalado/en ejecucion — situacion esperada en muchos clientes
          if (
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as any).message === 'string' &&
            ((err as any).message.includes('ERR_CONNECTION_REFUSED') ||
              (err as any).message.includes('CONNECTION_REFUSED'))
          ) {
            logger('QZ Tray no está disponible en esta máquina (esperado si no se usa impresora térmica).');
            return;
          }
          console.error('Error al conectar a QZ Tray:', err);
        });
    }
  }

  async isReady(): Promise<boolean> {
    if (!environment.qzTrayEnabled) return false;

    try {
      if (qz.websocket.isActive()) return true;
      await this.init();
      return qz.websocket.isActive();
    } catch {
      return false;
    }
  }

  private async getPrinters() {
    const result = await qz.printers.find();
    this.impresoras.next(typeof result === 'string' ? [result] : result);
  }

  async printData(
    printerName: string,
    data: any[] | string,
    options?: { cut?: boolean; feedLines?: number; format?: 'text' | 'html'; paperWidth?: number },
  ) {
    const format = options?.format || 'text';
    const config = qz.configs.create(printerName, {
      encoding: 'CP850',
    });

    if (format === 'html') {
      // Impresión HTML usando tipo 'pixel' con formato 'html'
      const htmlContent = typeof data === 'string' ? data : data.join('');

      // Conversión correcta de mm a píxeles CSS estándares
      // 1 pulgada = 96 píxeles CSS, 1 pulgada = 25.4mm
      const paperWidthMm = options?.paperWidth || 80;
      const mmToCssPixels = 96 / 25.4; // ~3.78 px/mm
      const pageWidth = Math.round(paperWidthMm * mmToCssPixels);

      // Crear objeto PrintData compatible con QZ Tray
      const printData = {
        type: 'pixel' as const,
        format: 'html' as const,
        data: htmlContent,
        flavor: 'plain' as const,
        pageWidth, // Opciones al nivel principal, no anidadas
        // pageHeight: 1400,
      };

      await qz.print(config, [printData]);
    } else {
      // Impresión raw ESC/POS (compatibilidad hacia atrás)
      const normalized = (data as any[]).map((line) =>
        line.replace(/\r?\n/g, '\r\n').endsWith('\r\n') ? line : line + '\r\n',
      );
      normalized.unshift('\x1B\x40'); // Reset impresora
      logger('Contenido a imprimir (raw):', options?.feedLines);
      normalized.push('\r\n'.repeat(options?.feedLines ?? 1)); // Avance de líneas
      if (options?.cut) {
        normalized.push('\x1D\x56\x00'); // Corte total
      }

      await qz.print(config, normalized);
    }
  }

  isVirtualPdfPrinter(printerName: string): boolean {
    const normalized = printerName.toLowerCase();
    return normalized.includes('microsoft print to pdf') || normalized.includes('pdf');
  }

  async printHtmlWithBrowserDialog(
    htmlContent: string,
    title = 'Documento',
    paperWidthMm = 80,
  ): Promise<void> {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';

    document.body.appendChild(iframe);

    const content = `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <style>
    @page {
      size: ${paperWidthMm}mm auto;
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      background: #fff;
    }
    body {
      display: flex;
      justify-content: center;
    }
    .vc-ticket-root {
      width: ${paperWidthMm}mm;
      max-width: ${paperWidthMm}mm;
      margin: 0;
      padding: 0;
      background: #fff;
    }
  </style>
</head>
<body>
  <div class="vc-ticket-root">${htmlContent}</div>
</body>
</html>`;

    iframe.srcdoc = content;

    await new Promise<void>((resolve, reject) => {
      iframe.onload = () => {
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) {
          reject(new Error('No se pudo acceder al frame de impresión'));
          return;
        }

        const cleanup = () => {
          setTimeout(() => {
            iframe.remove();
          }, 500);
          resolve();
        };

        frameWindow.onafterprint = cleanup;
        frameWindow.focus();
        frameWindow.print();

        setTimeout(cleanup, 2000);
      };

      iframe.onerror = () => {
        iframe.remove();
        reject(new Error('No se pudo cargar el contenido para impresión PDF'));
      };
    });
  }

  getPaperWidthCharsFromMm(mm: number): number {
    return mm <= 60 ? 32 : 48;
  }
}
